import { useEffect, useMemo, useState } from "react";
import { Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const addable = (p) => !p.is_box && !p.bookable && !(p.subscription || {}).enabled;

export default function OrderEditDialog({ order, onClose, onSaved }) {
  const [lines, setLines] = useState(() => order.items.map((it, index) => ({ index, name: it.name, unit: it.unit_amount_cents, quantity: it.quantity, detail: it.box_label || it.slot_label })));
  const [products, setProducts] = useState([]);
  const [pick, setPick] = useState("");
  const [refund, setRefund] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data.filter(addable))).catch(() => {}); }, []);

  const ship = order.shipping?.charge_cents || 0;
  const discounts = (order.loyalty_discount_cents || 0) + (order.welcome_discount_cents || 0);
  const total = useMemo(() => Math.max(0, lines.reduce((s, l) => s + l.unit * l.quantity, 0) + ship - discounts), [lines, ship, discounts]);
  const isPaid = order.payment_status === "paid";
  const paid = order.paid_cents ?? Math.round((order.amount || 0) * 100);
  const delta = total - paid;

  const setQty = (i, q) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, quantity: Math.max(0, q) } : l)));
  const addProduct = () => {
    const p = products.find((x) => x.product_id === pick);
    if (!p) return;
    setLines((prev) => [...prev, { product_id: p.product_id, name: p.name, unit: p.price_cents, quantity: 1, isNew: true }]);
    setPick("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { refund_difference: refund, lines: lines.map((l) => (l.isNew ? { product_id: l.product_id, quantity: l.quantity } : { index: l.index, quantity: l.quantity })) };
      const { data } = await api.put(`/admin/orders/${order.session_id}/items`, payload);
      toast.success(data.message);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Modification impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="order-edit-dialog" className="max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white sm:max-w-lg [&>*]:min-w-0">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-bark">Modifier la commande n° {order.session_id.slice(-8).toUpperCase()}</DialogTitle></DialogHeader>
        <ul className="space-y-2">
          {lines.map((l, i) => (
            <li key={l.isNew ? `new-${i}` : l.index} data-testid={`order-edit-line-${i}`} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${l.quantity === 0 ? "border-terra/30 bg-terra/5 opacity-60" : "border-amber-900/10"}`}>
              <span className="min-w-0 text-sm text-bark">
                <span className="block truncate font-medium">{l.name}</span>
                {l.detail && <span className="block truncate text-xs text-amber-deep">{l.detail}</span>}
                <span className="text-xs text-stone-mute">{formatPrice(l.unit)} / unité</span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                <button type="button" data-testid={`order-edit-minus-${i}`} onClick={() => setQty(i, l.quantity - 1)} className="rounded-full border border-amber-900/20 p-1 text-bark hover:border-amber-deep"><Minus size={13} /></button>
                <span data-testid={`order-edit-qty-${i}`} className="w-7 text-center text-sm font-semibold">{l.quantity}</span>
                <button type="button" data-testid={`order-edit-plus-${i}`} onClick={() => setQty(i, l.quantity + 1)} className="rounded-full border border-amber-900/20 p-1 text-bark hover:border-amber-deep"><Plus size={13} /></button>
                <button type="button" data-testid={`order-edit-remove-${i}`} onClick={() => setQty(i, 0)} className="ml-1 p-1 text-terra hover:text-terra/70"><Trash2 size={14} /></button>
              </span>
            </li>
          ))}
        </ul>
        <div className="flex min-w-0 gap-2">
          <select data-testid="order-edit-product-select" value={pick} onChange={(e) => setPick(e.target.value)} className="w-0 min-w-0 flex-1 rounded-full border border-amber-900/20 bg-cream px-3 py-2 text-sm text-bark outline-none focus:border-amber-deep">
            <option value="">Ajouter un produit…</option>
            {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} — {formatPrice(p.price_cents)} (stock {p.stock})</option>)}
          </select>
          <button type="button" data-testid="order-edit-add-product" disabled={!pick} onClick={addProduct} className="rounded-full bg-sand px-4 py-2 text-sm font-medium text-bark hover:bg-amber-100 disabled:opacity-50">Ajouter</button>
        </div>
        <div className="space-y-1 rounded-xl bg-cream px-4 py-3 text-sm text-bark">
          {ship > 0 && <p className="flex justify-between text-stone-mute"><span>Livraison (inchangée)</span><span>{formatPrice(ship)}</span></p>}
          {discounts > 0 && <p className="flex justify-between text-stone-mute"><span>Remises (inchangées)</span><span>−{formatPrice(discounts)}</span></p>}
          <p className="flex justify-between font-semibold"><span>Nouveau total</span><span data-testid="order-edit-total">{formatPrice(total)}</span></p>
          {isPaid && delta > 0 && <p data-testid="order-edit-delta" className="flex justify-between font-semibold text-amber-deep"><span>Reste à payer par le client</span><span>{formatPrice(delta)}</span></p>}
          {isPaid && delta < 0 && <p data-testid="order-edit-delta" className="flex justify-between font-semibold text-sage"><span>À rembourser au client</span><span>{formatPrice(-delta)}</span></p>}
          {!isPaid && <p className="text-xs text-stone-mute">Commande par chèque non encaissée : le montant du chèque attendu sera mis à jour.</p>}
        </div>
        {isPaid && delta < 0 && order.paypal_capture_id && (
          <label className="flex items-center gap-2 text-sm text-bark">
            <input data-testid="order-edit-refund" type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} className="h-4 w-4 accent-amber-deep" /> Rembourser la différence via PayPal
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" data-testid="order-edit-cancel" onClick={onClose} className="rounded-full border border-amber-900/20 px-5 py-2 text-sm text-bark hover:border-amber-deep">Fermer</button>
          <button type="button" data-testid="order-edit-save" disabled={busy || !lines.some((l) => l.quantity > 0)} onClick={save} className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2 text-sm font-semibold text-white hover:bg-honey disabled:opacity-60">
            {busy && <Loader2 size={14} className="animate-spin" />} Enregistrer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
